<template>
  <v-dialog
    v-model="visible"
    class="project-modal"
    scrollable
    max-width="750"
  >
    <v-card
      v-if="action === 'delete'"
      class="pa-6"
    >
      <v-card-title primary-title>
        <div class="headline darkestblue--text">
          Are you sure?
        </div>
        <v-card-actions>
          <v-btn
            :ripple="false"
            icon
            @click.stop="closeForm()"
          >
            <v-icon color="brightblue">
              close
            </v-icon>
          </v-btn>
        </v-card-actions>
      </v-card-title>

      <v-card-text>
        <v-card-text>
          <v-form
            ref="projectForm"
            v-model="validForm"
          >
            <v-container grid-list-xs>
              <v-row>
                <v-col>
                  <p class="body-1">
                    Deleting a project will remove the project from all active lists and dashboards. However, historical data will be preserved for reporting purposes. If you want to temporarily remove a project without deleting its data, consider archiving it instead.
                  </p>
                </v-col>
              </v-row>

              <!-- Identify the Project -->
              <v-row>
                <v-col>
                  <v-text-field
                    v-model="entry.project_name"
                    label="Engagement Name"
                    name="project_name"
                    type="textual"
                    readonly
                  />

                  <v-text-field
                    v-model="entry.project_code"
                    label="Project Code"
                    name="project_code"
                    type="textual"
                    width="150px"
                    readonly
                  />
                </v-col>
              </v-row>

              <!-- Confirmation Checkbox -->
              <v-row>
                <v-col>
                  <div
                    class="d-flex align-start"
                    role="button"
                    tabindex="0"
                    @click="toggleDeleteConfirmation"
                    @keydown.space.prevent="toggleDeleteConfirmation"
                    @keydown.enter.prevent="toggleDeleteConfirmation"
                  >
                    <Checkbox
                      :input-value="isProjectDeleted"
                      color="error"
                      @click.native.stop
                      @change="onDeleteConfirmationChange"
                    />
                    <div class="ml-2">
                      <span
                        class="body-2"
                        :class="{ 'error--text': deleteConfirmationError }"
                      >
                        I understand that this action cannot be undone.
                      </span>
                      <div
                        v-if="deleteConfirmationError"
                        class="caption error--text"
                      >
                        You must confirm to proceed with deletion.
                      </div>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>

        <v-card-actions class="justify-end">
          <v-btn
            text
            color="coolgray"
            @click.stop="cancelAction"
          >
            Cancel
          </v-btn>

          <v-btn
            color="primary"
            @click.stop="handleSubmitProject"
          >
            Submit
          </v-btn>
        </v-card-actions>
      </v-card-text>
    </v-card>

    <v-card
      v-if="['create', 'update'].includes(action)"
      class="pa-4"
    >
      <v-card-title>
        <span>
          {{ action === 'create' ? 'Add' : 'Edit' }} Engagement
        </span>

        <v-card-actions>
          <v-btn
            :ripple="false"
            icon
            @click.stop="closeForm()"
          >
            <v-icon color="brightblue">
              close
            </v-icon>
          </v-btn>
        </v-card-actions>
      </v-card-title>

      <v-card-text>
        <v-form
          ref="projectForm"
          v-model="validForm"
        >
          <!-- Form Validation Error Alert -->
          <v-alert
            v-if="showFormError"
            type="error"
            dense
            dismissible
            class="mb-4"
            @input="showFormError = false"
          >
            Please fix the errors below before submitting.
          </v-alert>

          <v-row class="mb-2">
            <v-col cols="12">
              <div
                class="d-flex align-center justify-space-between flex-wrap"
                style="gap: 8px;"
              >
                <div class="caption grey--text">
                  Step {{ formStage === 'essentials' ? '1' : '2' }} of 2
                </div>

                <v-btn-toggle
                  v-model="formStage"
                  mandatory
                  dense
                >
                  <v-btn
                    :value="'essentials'"
                    small
                    outlined
                    class="text-none"
                  >
                    Essentials
                  </v-btn>
                  <v-btn
                    :value="'details'"
                    small
                    outlined
                    class="text-none"
                  >
                    Details
                  </v-btn>
                </v-btn-toggle>
              </div>
            </v-col>
          </v-row>

          <v-container grid-list-xs>
            <!-- Contract Details -->
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="entry.contractType"
                  label="Contract Type"
                  name="contractType"
                  outlined
                  dense
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="entry.contractStatus"
                  label="Contract Status"
                  name="contractStatus"
                  outlined
                  dense
                />
              </v-col>
            </v-row>

            <v-row class="mb-2">
              <v-col
                cols="12"
                sm="4"
              >
                <v-text-field
                  v-model.number="entry.contractBillingRate"
                  label="Contract Billing Rate"
                  name="contractBillingRate"
                  type="number"
                  prefix="$"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
              <v-col
                cols="12"
                sm="4"
              >
                <v-text-field
                  v-model="entry.contractBillingType"
                  label="Contract Billing Type"
                  name="contractBillingType"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
              <v-col
                cols="12"
                sm="4"
              >
                <v-text-field
                  v-model="entry.contractBillingUnit"
                  label="Contract Billing Unit"
                  name="contractBillingUnit"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
            </v-row>

            <!-- Row 1: Billing Code + Project Code Suffix -->
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <v-autocomplete
                  v-model="entry.billingCode"
                  :items="clientItems"
                  :loading="loadingClients"
                  item-value="billingCode"
                  item-text="billingCode"
                  label="Billing Code"
                  :rules="billingCodeRules"
                  outlined
                  dense
                  autofocus
                  @change="onBillingCodeChange"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ item.billingCode }}
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{ item.clientName }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                  <template v-slot:append-inner>
                    <font-awesome-icon
                      v-if="entry.billingCode"
                      :icon="['fas', 'xmark']"
                      size="sm"
                      class="cursor-pointer grey--text mt-1"
                      @click="entry.billingCode = null; onBillingCodeChange()"
                    />
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="projectCodeSuffix"
                  label="Project Code Suffix"
                  :prefix="entry.billingCode ? `${entry.billingCode}` : ''"
                  :placeholder="suggestedSuffix || '_P2026-01'"
                  :rules="projectCodeSuffixRules"
                  :hint="fullProjectCode ? `${fullProjectCode}` : 'Enter suffix, e.g. _P2026-01'"
                  persistent-hint
                  outlined
                  dense
                  maxlength="9"
                  @input="onSuffixInput"
                >
                  <template v-slot:prepend-inner>
                    <span class="grey--text mt-3">P</span>
                  </template>

                  <template v-slot:append-inner>
                    <font-awesome-icon
                      v-if="projectCodeSuffix"
                      :icon="['fas', 'xmark']"
                      size="sm"
                      class="cursor-pointer grey--text mt-1"
                      @click="projectCodeSuffix = ''; syncProjectCode()"
                    />
                  </template>
                </v-text-field>
              </v-col>
            </v-row>

            <!-- Row 2: Engagement Name - Full Width -->
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="entry.project_name"
                  label="Engagement Name"
                  name="project_name"
                  :rules="projectNameRules"
                  outlined
                  dense
                >
                  <template v-slot:append-inner>
                    <font-awesome-icon
                      v-if="entry.project_name"
                      :icon="['fas', 'xmark']"
                      size="sm"
                      class="cursor-pointer grey--text mt-1"
                      @click="entry.project_name = ''"
                    />
                  </template>
                </v-text-field>
              </v-col>
            </v-row>

            <!-- Row 3: Parent Engagement (optional) -->
            <v-row>
              <v-col cols="12">
                <v-autocomplete
                  v-model="entry.parentProjectId"
                  :items="parentProjectItems"
                  item-text="label"
                  item-value="projectCode"
                  label="Parent Engagement (optional)"
                  hint="Leave blank for top-level engagements"
                  persistent-hint
                  clearable
                  outlined
                  dense
                >
                  <template v-slot:prepend-inner>
                    <font-awesome-icon
                      :icon="['fas', 'sitemap']"
                      size="sm"
                      class="grey--text mt-1"
                    />
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>

            <!-- Row 4: Est. Hours + Monthly Hours + Est. Value -->
            <v-row>
              <v-col
                cols="12"
                sm="4"
              >
                <v-text-field
                  v-model="entry.projectHours"
                  label="Est. Hours"
                  name="projectHours"
                  type="number"
                  :rules="projectHoursRules"
                  outlined
                  dense
                >
                  <template v-slot:append-inner>
                    <font-awesome-icon
                      v-if="entry.projectHours"
                      :icon="['fas', 'xmark']"
                      size="sm"
                      class="cursor-pointer grey--text mt-1"
                      @click="entry.projectHours = null"
                    />
                  </template>
                </v-text-field>
              </v-col>
              <v-col
                cols="12"
                sm="4"
              >
                <v-text-field
                  v-model.number="entry.monthlyHours"
                  label="Monthly Hours"
                  name="monthlyHours"
                  type="number"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
              <v-col
                cols="12"
                sm="4"
              >
                <v-text-field
                  v-model.number="entry.estimatedProjectValue"
                  label="Est. Value ($)"
                  name="estimatedProjectValue"
                  type="number"
                  prefix="$"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
            </v-row>

            <!-- Row 3b: Start Date + End Date -->
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <v-menu
                  v-model="startDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div
                      v-bind="attrs"
                      style="cursor: pointer"
                      v-on="on"
                    >
                      <v-text-field
                        v-model="entry.startDate"
                        label="Start Date"
                        readonly
                        outlined
                        dense
                      >
                        <template v-slot:prepend-inner>
                          <font-awesome-icon
                            :icon="['fas', 'calendar']"
                            class="grey--text"
                          />
                        </template>
                        <template v-slot:append-inner>
                          <font-awesome-icon
                            v-if="entry.startDate"
                            :icon="['fas', 'xmark']"
                            size="sm"
                            class="cursor-pointer grey--text mt-1"
                            @click.stop="entry.startDate = null"
                          />
                        </template>
                      </v-text-field>
                    </div>
                  </template>
                  <v-date-picker
                    v-model="entry.startDate"
                    @input="startDateMenu = false"
                  />
                </v-menu>
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-menu
                  v-model="endDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div
                      v-bind="attrs"
                      style="cursor: pointer"
                      v-on="on"
                    >
                      <v-text-field
                        v-model="entry.endDate"
                        label="End Date"
                        readonly
                        outlined
                        dense
                      >
                        <template v-slot:prepend-inner>
                          <font-awesome-icon
                            :icon="['fas', 'calendar']"
                            class="grey--text"
                          />
                        </template>
                        <template v-slot:append-inner>
                          <font-awesome-icon
                            v-if="entry.endDate"
                            :icon="['fas', 'xmark']"
                            size="sm"
                            class="cursor-pointer grey--text mt-1"
                            @click.stop="entry.endDate = null"
                          />
                        </template>
                      </v-text-field>
                    </div>
                  </template>
                  <v-date-picker
                    v-model="entry.endDate"
                    :min="entry.startDate"
                    @input="endDateMenu = false"
                  />
                </v-menu>
              </v-col>
            </v-row>

            <!-- Type & Status - Side by Side -->
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <v-autocomplete
                  v-model="entry.projectTypes"
                  :items="projectTypesWithCreateOption"
                  :filter="filterProjectTypes"
                  item-text="title"
                  item-value="value"
                  label="Project Type"
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  :loading="isLoadingProjectTypes"
                  outlined
                  dense
                  @change="onProjectTypeChange"
                >
                  <template v-slot:item="{ item, attrs, on }">
                    <v-list-item
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-list-item-action class="mr-2">
                        <font-awesome-icon
                          :icon="isTypeSelected(item) ? ['fas', 'square-check'] : ['far', 'square']"
                          :class="isTypeSelected(item) ? 'primary--text' : 'grey--text'"
                        />
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>{{ item.title }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-autocomplete
                  v-model="entry.projectStatus"
                  :items="projectStatusesWithFallback"
                  item-text="title"
                  item-value="value"
                  label="Status"
                  :loading="isLoadingProjectStatuses"
                  outlined
                  dense
                >
                  <template v-slot:append-inner>
                    <font-awesome-icon
                      v-if="entry.projectStatus"
                      :icon="['fas', 'xmark']"
                      size="sm"
                      class="cursor-pointer grey--text mt-1"
                      @click="entry.projectStatus = null"
                    />
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>

            <v-alert
              :type="lifecycleHintHasWarning ? 'warning' : 'info'"
              dense
              text
              class="mb-3"
            >
              <div class="body-2 mb-1">
                {{ lifecycleHintHasWarning ? 'Please resolve before submit:' : 'Lifecycle checks:' }}
              </div>
              <div
                v-for="(hint, index) in lifecycleHintItems"
                :key="`lifecycle-hint-${index}`"
                class="d-flex align-center mb-1"
              >
                <font-awesome-icon
                  :icon="hint.isWarning ? ['fas', 'circle-exclamation'] : ['fas', 'circle-check']"
                  size="sm"
                  :class="hint.isWarning ? 'warning--text mr-2' : 'success--text mr-2'"
                />
                <span :class="hint.isWarning ? 'warning--text' : ''">{{ hint.text }}</span>
              </div>
            </v-alert>

            <!-- Platforms - Full Width -->
            <template v-if="formStage === 'details'">
              <v-row>
                <v-col cols="12">
                  <v-autocomplete
                    v-model="entry.projectPlatforms"
                    :items="projectPlatformsWithFallback"
                    item-text="title"
                    item-value="value"
                    label="Platforms"
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    :loading="isLoadingProjectPlatforms"
                    hint="Select one or more platforms"
                    persistent-hint
                    outlined
                    dense
                  >
                    <template v-slot:item="{ item, attrs, on }">
                      <v-list-item
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-list-item-action class="mr-2">
                          <font-awesome-icon
                            :icon="isPlatformSelected(item) ? ['fas', 'square-check'] : ['far', 'square']"
                            :class="isPlatformSelected(item) ? 'primary--text' : 'grey--text'"
                          />
                        </v-list-item-action>
                        <v-list-item-content>
                          <v-list-item-title>{{ item.title }}</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>

              <!-- Project Owner -->
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="entry.projectOwner"
                    label="Project Owner"
                    name="projectOwner"
                    outlined
                    dense
                    hint="Email or name of the project owner"
                    persistent-hint
                  />
                </v-col>
              </v-row>

              <!-- Description - Full Width -->
              <v-row>
                <v-col cols="12">
                  <v-textarea
                    v-model="entry.projectDescription"
                    auto-grow
                    counter="500"
                    label="Description (optional)"
                    name="description"
                    type="textual"
                    :rules="descriptionRules"
                    row-height="15"
                    rows="2"
                    outlined
                    dense
                    @input="validateForm"
                  >
                    <template v-slot:append-inner>
                      <font-awesome-icon
                        v-if="entry.projectDescription"
                        :icon="['fas', 'xmark']"
                        size="sm"
                        class="cursor-pointer grey--text mt-1"
                        @click="entry.projectDescription = ''"
                      />
                    </template>
                  </v-textarea>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12">
                  <div class="d-flex align-center justify-space-between mb-2">
                    <div class="body-2 font-weight-medium">
                      Project Docs
                    </div>

                    <v-btn
                      outlined
                      small
                      class="text-none"
                      @click="addProjectDoc"
                    >
                      <font-awesome-icon
                        :icon="['fas', 'plus']"
                        size="sm"
                        class="mr-1"
                      />
                      Add Link
                    </v-btn>
                  </div>

                  <div
                    v-if="normalizedEntryDocs.length === 0"
                    class="caption grey--text mb-2"
                  >
                    Add Google Drive or other project asset links such as SOWs and briefs.
                  </div>

                  <v-row
                    v-for="(doc, index) in normalizedEntryDocs"
                    :key="`project-doc-${index}`"
                    class="mb-1"
                  >
                    <v-col
                      cols="12"
                      sm="4"
                    >
                      <v-text-field
                        v-model="entry.docs[index].title"
                        label="Link Label"
                        outlined
                        dense
                      />
                    </v-col>
                    <v-col
                      cols="12"
                      sm="7"
                    >
                      <v-text-field
                        v-model="entry.docs[index].url"
                        label="URL"
                        outlined
                        dense
                      />
                    </v-col>
                    <v-col
                      cols="12"
                      sm="1"
                      class="d-flex align-center justify-center"
                    >
                      <v-btn
                        icon
                        @click="removeProjectDoc(index)"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'trash-can']"
                          size="sm"
                        />
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>

              <!-- Advanced Options - Collapsible -->
              <v-row>
                <v-col cols="12">
                  <v-expansion-panels flat>
                    <v-expansion-panel>
                      <v-expansion-panel-header class="pa-0">
                        <span class="text-caption grey--text">
                          <font-awesome-icon
                            :icon="['fas', 'cog']"
                            class="mr-2"
                          />
                          Advanced Options
                        </span>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-row v-if="showThresholdFeature">
                          <v-col cols="12">
                            <v-btn
                              text
                              small
                              @click="showThresholdForm('create')"
                            >
                              <font-awesome-icon
                                :icon="['fas', 'plus']"
                                class="mr-2"
                              />
                              Add Threshold
                            </v-btn>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col
                            cols="12"
                            sm="6"
                          >
                            <div class="d-flex align-center">
                              <Checkbox
                                :input-value="isProjectArchived"
                                color="warning"
                                @change="isProjectArchived = $event"
                              />
                              <div class="ml-2">
                                <span class="body-2">Archive this project</span>
                                <div class="caption grey--text">
                                  Hidden from active lists
                                </div>
                              </div>
                            </div>
                          </v-col>
                          <v-col
                            cols="12"
                            sm="6"
                          >
                            <div class="d-flex align-center">
                              <Checkbox
                                :input-value="isProjectDeleted"
                                color="error"
                                @change="isProjectDeleted = $event"
                              />
                              <div class="ml-2">
                                <span class="body-2">Mark as deleted</span>
                                <div class="caption grey--text">
                                  Data preserved for reporting
                                </div>
                              </div>
                            </div>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col
                            cols="12"
                            sm="6"
                          >
                            <div class="d-flex align-center">
                              <Checkbox
                                :input-value="isProjectTest"
                                color="info"
                                @change="isProjectTest = $event"
                              />
                              <div class="ml-2">
                                <span class="body-2">Test project</span>
                                <div class="caption grey--text">
                                  Hidden from non-admin users
                                </div>
                              </div>
                            </div>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col
                            cols="12"
                            sm="6"
                          >
                            <div class="d-flex align-center">
                              <Checkbox
                                :input-value="isProjectPublished"
                                color="primary"
                                @change="isProjectPublished = $event"
                              />
                              <div class="ml-2">
                                <span class="body-2">Published</span>
                                <div class="caption grey--text">
                                  Visible to all team members when published
                                </div>
                              </div>
                            </div>
                          </v-col>
                        </v-row>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-col>
              </v-row>
            </template>
          </v-container>
        </v-form>

        <v-row>
          <v-col>
            <v-card-actions class="justify-end">
              <v-btn
                text
                color="coolgray"
                @click.stop="cancelAction"
              >
                Cancel
              </v-btn>

              <v-btn
                v-if="formStage === 'essentials'"
                color="primary"
                class="text-none"
                @click.stop="formStage = 'details'"
              >
                Continue to Details
              </v-btn>

              <v-btn
                v-else
                color="primary"
                :disabled="!validForm"
                @click.stop="handleSubmitProject"
              >
                Submit
              </v-btn>
            </v-card-actions>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Assign Client (compact form) -->
    <v-card
      v-if="action === 'assign-client'"
      class="pa-4"
    >
      <v-card-title>
        <span>Assign Client</span>

        <v-card-actions>
          <v-btn
            :ripple="false"
            icon
            @click.stop="closeForm()"
          >
            <v-icon color="brightblue">
              close
            </v-icon>
          </v-btn>
        </v-card-actions>
      </v-card-title>

      <v-card-text>
        <v-form
          ref="assignClientForm"
          v-model="validForm"
        >
          <v-container grid-list-xs>
            <!-- Project code (read-only context) -->
            <v-row>
              <!-- Billing Code selector -->
              <v-col cols="6">
                <v-autocomplete
                  v-model="entry.billingCode"
                  :items="clientItems"
                  :loading="loadingClients"
                  item-value="billingCode"
                  item-text="billingCode"
                  label="Billing Code"
                  :rules="billingCodeRules"
                  outlined
                  dense
                  autofocus
                  @change="onBillingCodeChange"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ item.billingCode }}
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{ item.clientName }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                  <template v-slot:append-inner>
                    <font-awesome-icon
                      v-if="entry.billingCode"
                      :icon="['fas', 'xmark']"
                      size="sm"
                      class="cursor-pointer grey--text mt-1"
                      @click="entry.billingCode = null; onBillingCodeChange()"
                    />
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="6">
                <v-text-field
                  v-model="projectCodeSuffix"
                  label="Project Code Suffix"
                  :prefix="entry.billingCode ? `${entry.billingCode}` : ''"
                  :placeholder="suggestedSuffix || '_P2026-01'"
                  :rules="projectCodeSuffixRules"
                  :hint="fullProjectCode ? `${fullProjectCode}` : 'Enter suffix, e.g. _P2026-01'"
                  persistent-hint
                  outlined
                  dense
                  maxlength="9"
                  @input="onSuffixInput"
                />
              </v-col>
            </v-row>

            <!-- Project name (read-only context) -->
            <v-row>
              <v-col cols="12">
                <v-text-field
                  :value="entry.project_name || entry.projectName"
                  label="Project"
                  readonly
                  outlined
                  dense
                  hide-details
                />
              </v-col>
            </v-row>

            <!-- Client ID hint -->
            <v-row
              v-if="entry.billingCode && assignClientHint"
              class="mt-n4"
            >
              <v-col cols="12">
                <span class="caption grey--text">{{ assignClientHint }}</span>
              </v-col>
            </v-row>
          </v-container>
        </v-form>

        <v-card-actions class="justify-end">
          <v-btn
            text
            color="coolgray"
            @click.stop="cancelAction"
          >
            Cancel
          </v-btn>

          <v-btn
            color="primary"
            :disabled="!validForm || !entry.billingCode"
            @click.stop="submitAssignClient"
          >
            Assign
          </v-btn>
        </v-card-actions>
      </v-card-text>
    </v-card>

    <!-- Create Project Type Dialog -->
    <v-dialog
      v-model="showCreateProjectTypeDialog"
      max-width="500"
      persistent
    >
      <v-card class="pa-4">
        <v-card-title>
          <span>Create Project Type</span>
          <v-spacer />
          <v-btn
            icon
            @click="closeCreateProjectTypeDialog"
          >
            <v-icon color="brightblue">
              close
            </v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-form
            ref="projectTypeForm"
            v-model="validProjectTypeForm"
          >
            <v-text-field
              v-model="newProjectType.value"
              label="Value (required)"
              :rules="projectTypeValueRules"
              hint="Back-end value"
              persistent-hint
              required
            />

            <v-text-field
              v-model="newProjectType.title"
              label="Title"
              hint="Front-end label. Uses value if empty."
              persistent-hint
              :rules="projectTypeTitleRules"
            />

            <v-textarea
              v-model="newProjectType.description"
              label="Description"
              auto-grow
              rows="15"
              counter="500"
              :rules="projectTypeDescriptionRules"
            />
          </v-form>
        </v-card-text>

        <v-card-actions class="justify-end">
          <v-btn
            text
            color="coolgray"
            @click="closeCreateProjectTypeDialog"
          >
            Cancel
          </v-btn>

          <v-btn
            color="primary"
            :loading="creatingProjectType"
            :disabled="!validProjectTypeForm"
            @click="createProjectType"
          >
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import Checkbox from '@/tools/components/Checkbox'
import api from '@/store/api.js'
import { validateEngagementLifecycleTransition } from '@/internal/utils/engagementLifecycle'

export default {
  name: 'ProjectModal',
  components: {
    Checkbox,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    loading: false,
    validForm: true,
    showThresholdFeature: false,
    thresholdForm: null,

    // Client data for billing code dropdown
    clientItems: [],
    loadingClients: false,

    // Project code suffix (the _P{year}-{incrementer} part)
    projectCodeSuffix: '',

    // Date picker menus
    startDateMenu: false,
    endDateMenu: false,

    // Delete confirmation
    deleteConfirmationError: false,

    // Form validation error display
    showFormError: false,
    formStage: 'essentials',

    // Project Type Creation
    showCreateProjectTypeDialog: false,
    validProjectTypeForm: false,
    creatingProjectType: false,
    newProjectType: {
      title: '',
      value: '',
      description: '',
    },
  }),
  computed: {
    visible: {
      get() {
        return this.isProjectModalVisible
      },
      set(value) {
        this.$store.commit('internal/isProjectModalVisible', value)
      },
    },
    action() {
      return this.$store.state.internal?.action
    },
    didLoadProjectFail() {
      return this.$store.state.internal?.didLoadProjectFail
    },
    entry() {
      return this.$store.state.internal?.entry
    },
    isLoadingProject() {
      return this.$store.state.internal?.isLoadingProject
    },
    isProjectModalVisible() {
      return this.$store.state.internal?.isProjectModalVisible
    },
    projects() {
      return this.$store.state.internal?.projects
    },
    selected() {
      return this.$store.state.internal?.selected
    },
    errorMessage() {
      return this.$store.getters['internal/errorMessage']
    },
    successMessage() {
      return `Project ${this.$store.getters['internal/successMessage']}`
    },
    // Loading states from tools store
    isLoadingProjectTypes() {
      return this.$store.state.tools?.isLoadingProjectTypes || false
    },
    isLoadingProjectStatuses() {
      return this.$store.state.tools?.isLoadingProjectStatuses || false
    },
    isLoadingProjectPlatforms() {
      return this.$store.state.tools?.isLoadingProjectPlatforms || false
    },
    /**
     * Computed full project code = billingCode + suffix.
     * Example: "BBCM2025" + "_P2025-01" = "BBCM2025_P2025-01"
     */
    fullProjectCode() {
      const billing = this.entry?.billingCode || ''
      const suffix = this.projectCodeSuffix || ''
      if (!billing) return suffix
      return `${billing}${suffix}`
    },
    /**
     * Auto-suggest the next available suffix for the selected billing code.
     * Scans existing project codes that start with the billing code and
     * proposes the next incrementer.
     */
    suggestedSuffix() {
      const billing = this.entry?.billingCode
      if (!billing) return ''

      const year = new Date().getFullYear()
      const prefix = `${billing}_P${year}-`
      const allProjects = (
        this.$store.getters['tools/storedItems']?.projects?.all
        || this.$store.getters['tools/projects']
        || []
      )
      const currentId = this.entry?.project_id || this.entry?.projectId

      // Ensure allProjects is an array before calling forEach
      const projectsArray = Array.isArray(allProjects) ? allProjects : []
      let maxIncrementer = 0
      projectsArray.forEach((p) => {
        if (p.isDeleted || p.project_id === currentId) return
        const code = (p.projectCode || p.project_code || '').toUpperCase()
        if (code.startsWith(prefix.toUpperCase())) {
          const tail = code.substring(prefix.length)
          const num = parseInt(tail, 10)
          if (!Number.isNaN(num) && num > maxIncrementer) {
            maxIncrementer = num
          }
        }
      })

      const next = String(maxIncrementer + 1).padStart(2, '0')
      return `_P${year}-${next}`
    },
    // Billing code validation
    billingCodeRules() {
      return [
        (value) => !!value || 'Billing code is required.',
      ]
    },
    assignClientHint() {
      if (!this.entry?.billingCode) return ''
      const client = this.clientItems.find((c) => c.billingCode === this.entry.billingCode)
      if (client) return `Client: ${client.clientName}`
      return ''
    },
    // Project code suffix validation
    projectCodeSuffixRules() {
      return [
        (value) => {
          if (!value) return true // suffix can be empty
          // Must match _P{year}-{incrementer} or similar short patterns
          if (value.length > 9) return 'Suffix cannot exceed 9 characters.'
          return true
        },
        (value) => {
          if (!value) return true
          // Restrict to safe characters: alphanumeric, underscore, hyphen
          if (!/^[A-Za-z0-9_-]+$/.test(value)) return 'Only letters, numbers, hyphens, and underscores allowed.'
          return true
        },
        // Full project code uniqueness check
        (value) => {
          if (!value || !this.entry?.billingCode) return true
          const full = this.fullProjectCode
          if (full && this.existingProjectCodes.has(full.toLowerCase())) {
            return 'A project with this code already exists.'
          }
          return true
        },
        // Full code length limit (17 chars max)
        (value) => {
          if (!value || !this.entry?.billingCode) return true
          if (this.fullProjectCode.length > 17) {
            return `Full code would be ${this.fullProjectCode.length} chars (max 17).`
          }
          return true
        },
      ]
    },
    // Get data from tools store (where fetchProjectTypes/Statuses/Platforms commit to)
    projectTypes() {
      const toolsProjectTypes = this.$store.getters['tools/projectTypes'] || []
      return toolsProjectTypes.map((doc) => ({
        title: doc.title || doc.text || doc.value,
        value: doc.value,
      }))
    },
    projectStatuses() {
      const toolsProjectStatuses = this.$store.getters['tools/projectStatuses'] || []
      return toolsProjectStatuses.map((doc) => ({
        title: doc.title || doc.text || doc.value,
        value: doc.value,
      }))
    },
    projectPlatforms() {
      const toolsProjectPlatforms = this.$store.state.tools?.projectPlatforms || []
      return toolsProjectPlatforms.map((doc) => ({
        title: doc.title || doc.text || doc.value,
        value: doc.value,
      }))
    },
    // Fallback options when Firebase collections are empty
    defaultProjectStatuses() {
      return [
        { title: 'Active', value: 'active' },
        { title: 'On Hold', value: 'on-hold' },
        { title: 'Complete', value: 'complete' },
        { title: 'Cancelled', value: 'cancelled' },
        { title: 'Planning', value: 'planning' },
      ]
    },
    defaultProjectPlatforms() {
      return [
        { title: 'Google Ad Manager', value: 'gam' },
        { title: 'DV360', value: 'dv360' },
        { title: 'Meta', value: 'meta' },
        { title: 'Amazon DSP', value: 'amazon-dsp' },
        { title: 'The Trade Desk', value: 'ttd' },
      ]
    },
    // Use Firebase data if available, otherwise fallback to defaults
    projectStatusesWithFallback() {
      if (this.isLoadingProjectStatuses) return []
      return this.projectStatuses.length > 0 ? this.projectStatuses : this.defaultProjectStatuses
    },
    projectPlatformsWithFallback() {
      if (this.isLoadingProjectPlatforms) return []
      return this.projectPlatforms.length > 0 ? this.projectPlatforms : this.defaultProjectPlatforms
    },
    parentProjectItems() {
      const currentCode = this.entry?.projectCode || this.entry?.project_code
      const allProjects = this.$store.getters['tools/projects'] || []
      return allProjects
        .filter((p) => !p.isDeleted && !p.isArchived && p.projectCode !== currentCode)
        .map((p) => ({
          projectCode: p.projectCode,
          label: p.projectName
            ? `${p.projectName} (${p.projectCode || 'no code'})`
            : p.projectCode || p.docId,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    },
    viewProject() {
      const {
        projects,
        didLoadProjectFail,
        isLoadingProject,
      } = this

      return isLoadingProject === false
        && didLoadProjectFail === false
        && projects.length > 0
    },
    noProjectData() {
      const {
        projects,
        isLoadingProject,
        didLoadProjectFail,
      } = this
      const requestComplete = isLoadingProject === false
      const requestSucceeded = didLoadProjectFail === false
      const noResults = projects.length === 0

      return requestComplete && requestSucceeded && noResults
    },
    descriptionRules() {
      return [
        (value) => {
          const valid = !value || value.length <= 500
          return valid || 'Description cannot exceed 500 characters.'
        },
      ]
    },
    // Project name validation — required, length, and injection prevention
    projectNameRules() {
      return [
        (value) => !!value || 'Engagement name is required.',
        (value) => {
          if (!value) return true
          if (value.length < 2) return 'Engagement name must be at least 2 characters.'
          return true
        },
        (value) => {
          if (!value) return true
          if (value.length > 200) return 'Engagement name cannot exceed 200 characters.'
          return true
        },
        // Block HTML/script injection patterns
        (value) => {
          if (!value) return true
          if (/<[^>]*>/i.test(value)) return 'HTML tags are not allowed.'
          return true
        },
        // Block javascript: protocol and event handlers
        (value) => {
          if (!value) return true
          if (/javascript\s*:/i.test(value)) return 'Invalid characters detected.'
          if (/on\w+\s*=/i.test(value)) return 'Invalid characters detected.'
          return true
        },
      ]
    },
    // Set of existing project codes for duplicate detection
    existingProjectCodes() {
      const allProjects = (
        this.$store.getters['tools/storedItems']?.projects?.all
        || this.$store.getters['tools/projects']
        || []
      )
      const currentProjectId = this.entry?.project_id || this.entry?.projectId
      // Ensure allProjects is an array before calling filter/map
      const projectsArray = Array.isArray(allProjects) ? allProjects : []
      return new Set(
        projectsArray
          .filter((p) => !p.isDeleted && p.projectCode && (p.projectId || p.project_id) !== currentProjectId)
          .map((p) => p.projectCode.toLowerCase()),
      )
    },
    // Project hours validation (17 character database limit)
    projectHoursRules() {
      return [
        (value) => {
          if (!value && value !== 0) return true // Allow empty
          const strValue = String(value)
          if (strValue.length > 17) return 'Project hours cannot exceed 17 characters.'
          return true
        },
        (value) => {
          if (!value && value !== 0) return true // Allow empty
          const numValue = Number(value)
          if (Number.isNaN(numValue)) return 'Please enter a valid number.'
          if (numValue < 0) return 'Project hours cannot be negative.'
          return true
        },
      ]
    },
    projectTypesWithCreateOption() {
      return [
        ...this.projectTypes,
        {
          title: '+ Create New Project Type',
          value: '__create_new__',
        },
        {
          title: '⚙️ Manage Project Types',
          value: '__manage__',
        },
      ]
    },
    normalizedEntryDocs() {
      const docs = Array.isArray(this.entry?.docs) ? this.entry.docs : []
      return docs.map((doc) => ({
        title: doc?.title || doc?.name || '',
        url: doc?.url || doc?.href || '',
      }))
    },
    lifecycleHintItems() {
      const normalize = (value) => String(value || '').trim().toLowerCase()
      const unsignedStatuses = new Set(['draft', 'scoping', 'in-negotiation', 'in negotiation', 'planning', 'proposed'])
      const signedStatuses = new Set(['in-progress', 'in progress', 'active', 'launching', 'on-hold', 'on hold', 'complete', 'closed'])

      const previousStatus = this.action === 'update' ? normalize(this.selected?.projectStatus) : ''
      const nextStatus = normalize(this.entry?.projectStatus)
      const isSigningTransition = unsignedStatuses.has(previousStatus) && signedStatuses.has(nextStatus)

      const billingCode = String(this.entry?.billingCode || '').trim()
      const projectCode = String(this.entry?.projectCode || this.entry?.project_code || '').trim()
      const estimatedValueRaw = this.entry?.estimatedProjectValue
      const hasEstimatedValue = estimatedValueRaw !== null
        && estimatedValueRaw !== undefined
        && estimatedValueRaw !== ''
        && Number.isFinite(Number(estimatedValueRaw))

      const hasSowDoc = this.normalizedEntryDocs.some((doc) => {
        const title = normalize(doc?.title)
        const url = normalize(doc?.url)
        return title.includes('statement of work')
          || title.includes('sow')
          || url.includes('statement-of-work')
          || url.includes('/sow')
      })

      return [
        {
          text: 'Project code requires a billing code.',
          isWarning: Boolean(projectCode && !billingCode),
        },
        {
          text: 'Unsigned to signed status requires a billing code.',
          isWarning: Boolean(isSigningTransition && !billingCode),
        },
        {
          text: 'SOW documents require an estimated value.',
          isWarning: Boolean(hasSowDoc && !hasEstimatedValue),
        },
      ]
    },
    lifecycleHintHasWarning() {
      return this.lifecycleHintItems.some((hint) => hint.isWarning)
    },
    projectTypeTitleRules() {
      return [
        (value) => !value || value.length >= 2 || 'Title must be at least 2 characters if provided',
        (value) => !value || value.length <= 50 || 'Title must be 50 characters or less',
      ]
    },
    projectTypeValueRules() {
      return [
        (value) => !!value || 'Project type code is required',
        (value) => /^[a-z0-9-_]+$/.test(value) || 'Code can only contain lowercase letters, numbers, hyphens, and underscores',
        (value) => (value && value.length >= 2) || 'Code must be at least 2 characters',
        (value) => (value && value.length <= 30) || 'Code must be 30 characters or less',
        (value) => !this.projectTypes.some((pt) => pt.value === value) || 'This code already exists',
      ]
    },
    projectTypeDescriptionRules() {
      return [
        (value) => !value || value.length <= 500 || 'Description must be 500 characters or less',
      ]
    },
    isProjectArchived: {
      get() {
        return Boolean(this.entry && this.entry.isArchived)
      },
      set(value) {
        if (this.entry) {
          this.entry.isArchived = Boolean(value)
        }
      },
    },
    isProjectActive: {
      get() {
        return Boolean(this.entry && this.entry.isActive)
      },
      set(value) {
        if (this.entry) {
          this.entry.isActive = Boolean(value)
        }
      },
    },
    isProjectDeleted: {
      get() {
        return Boolean(this.entry && this.entry.isDeleted)
      },
      set(value) {
        if (this.entry) {
          this.entry.isDeleted = Boolean(value)
        }
      },
    },
    isProjectTest: {
      get() {
        return Boolean(this.entry && this.entry.isTest)
      },
      set(value) {
        if (this.entry) {
          this.entry.isTest = Boolean(value)
        }
      },
    },
    isProjectPublished: {
      get() {
        // Default to true when field is absent (existing projects are treated as published)
        if (!this.entry) return true
        return this.entry.isPublished !== false
      },
      set(value) {
        if (!this.entry) return
        this.entry.isPublished = Boolean(value)
        if (value && !this.entry.publishedAt) {
          this.entry.publishedAt = new Date().toISOString()
          this.entry.publishedBy = this.$store.getters['auth/userEmail'] || null
        } else if (!value) {
          this.entry.publishedAt = null
          this.entry.publishedBy = null
        }
      },
    },
  },
  watch: {
    visible(isOpen) {
      if (isOpen) {
        this.formStage = 'essentials'
        this.normalizeEntryProjectFields()

        // When editing, extract the suffix from existing project code
        const code = this.entry?.project_code || ''
        const billing = this.entry?.billingCode || ''
        if (code && billing && code.startsWith(billing)) {
          this.projectCodeSuffix = code.substring(billing.length)
        } else if (code && !billing) {
          // No billing code yet — try to extract suffix from auto-generated pattern
          const match = code.match(/^(.+?)(_P\d{4}-\d{2})$/)
          if (match) {
            // Code looks like {billing}_P{year}-{seq} — extract just the suffix
            const [, , suffix] = match
            this.projectCodeSuffix = suffix
          } else {
            this.projectCodeSuffix = code
          }
        } else {
          this.projectCodeSuffix = ''
        }
      }

      if (!isOpen) {
        this.formStage = 'essentials'
      }
    },
  },
  async created() {
    // Fetch project types, statuses, platforms, and clients
    await Promise.all([
      this.$store.dispatch('tools/fetchProjectTypes', undefined, { root: true }),
      this.$store.dispatch('tools/fetchProjectStatuses', undefined, { root: true }),
      this.$store.dispatch('tools/fetchProjectPlatforms', undefined, { root: true }),
      this.fetchClients(),
    ])
  },
  methods: {
    /**
     * Fetch clients from the API for the billing code dropdown.
     */
    async fetchClients() {
      this.loadingClients = true
      try {
        const response = await api.getClients()
        this.clientItems = (response?.content || []).filter((c) => c.billingCode)
      } catch {
        this.clientItems = []
      } finally {
        this.loadingClients = false
      }
    },

    /**
     * When billing code changes, auto-suggest a suffix and sync project code.
     */
    onBillingCodeChange() {
      if (!this.entry) return

      // Sync clientId from the selected billing code
      const { billingCode } = this.entry
      const client = this.clientItems.find((c) => c.billingCode === billingCode)
      this.entry.clientId = client?.clientId || null

      if (this.action === 'create') {
        // Auto-fill suffix with suggested value when creating a new project
        if (this.suggestedSuffix && !this.projectCodeSuffix) {
          this.projectCodeSuffix = this.suggestedSuffix
        }
      } else {
        // When editing: update the suffix if the current code looks auto-generated
        // or if the suffix still contains the old billing code (double-prefix scenario)
        const currentFull = this.fullProjectCode
        if (!currentFull || this.isAutoPopulatedProjectCode(currentFull)) {
          this.projectCodeSuffix = this.suggestedSuffix || ''
        }
      }

      this.syncProjectCode()
    },

    /**
     * Returns true if the value matches an auto-generated project code pattern.
     */
    isAutoPopulatedProjectCode(value) {
      if (!value) return false
      // Matches any billing code prefix (bare billing code from the client list)
      if (this.clientItems.some((c) => c.billingCode === value)) return true
      // Matches an auto-generated project code pattern: {billing}_P{year}-{seq}
      return /^.+_P\d{4}-\d{2}$/.test(value)
    },

    /**
     * When suffix changes, sync the full project code to entry.
     */
    onSuffixInput() {
      this.syncProjectCode()
    },

    /**
     * Build the full project_code from billingCode + suffix and set it on entry.
     */
    syncProjectCode() {
      if (!this.entry) return
      this.entry.project_code = this.fullProjectCode || ''
    },

    normalizeProjectDocs(docs) {
      if (!Array.isArray(docs)) {
        return []
      }

      return docs
        .map((doc) => ({
          title: doc?.title || doc?.name || '',
          url: doc?.url || doc?.href || '',
        }))
        .filter((doc) => doc.title || doc.url)
    },

    normalizeEntryProjectFields() {
      if (!this.entry) return

      const getProjectTypeValue = () => (this.entry.projectType ? [this.entry.projectType] : [])

      const projectTypes = Array.isArray(this.entry.projectTypes) && this.entry.projectTypes.length > 0
        ? this.entry.projectTypes.filter(Boolean)
        : getProjectTypeValue()

      this.entry.projectTypes = [...new Set(projectTypes)]
      this.entry.projectType = this.entry.projectTypes[0] || null
      this.entry.docs = this.normalizeProjectDocs(this.entry.docs)
    },

    addProjectDoc() {
      if (!Array.isArray(this.entry.docs)) {
        this.$set(this.entry, 'docs', [])
      }

      this.entry.docs.push({ title: '', url: '' })
    },

    removeProjectDoc(index) {
      if (!Array.isArray(this.entry.docs)) return
      this.entry.docs.splice(index, 1)
    },

    /**
     * Toggle delete confirmation from row clicks or keyboard interaction.
     */
    toggleDeleteConfirmation() {
      this.onDeleteConfirmationChange(!this.isProjectDeleted)
    },

    /**
     * Handle delete confirmation checkbox change.
     * Clears error state when user confirms.
     */
    onDeleteConfirmationChange(value) {
      this.isProjectDeleted = value
      if (value) {
        this.deleteConfirmationError = false
      }
    },

    /**
     * Custom filter for Project Type autocomplete.
     * Always shows the special "Create" and "Manage" items.
     */
    filterProjectTypes(item, queryText) {
      if (item.value === '__create_new__' || item.value === '__manage__') return true
      const text = (item.title || '').toLowerCase()
      return text.includes((queryText || '').toLowerCase())
    },

    isTypeSelected(item) {
      if (!this.entry?.projectTypes) return false
      return this.entry.projectTypes.some(
        (t) => (typeof t === 'object' ? t.value : t) === (typeof item === 'object' ? item.value : item),
      )
    },

    isPlatformSelected(item) {
      if (!this.entry?.projectPlatforms) return false
      return this.entry.projectPlatforms.some(
        (p) => (typeof p === 'object' ? p.value : p) === (typeof item === 'object' ? item.value : item),
      )
    },

    onProjectTypeChange(value) {
      const getNextValue = () => (value ? [value] : [])

      const nextValues = Array.isArray(value) ? value.filter(Boolean) : getNextValue()

      const shouldCreate = nextValues.includes('__create_new__')
      const shouldManage = nextValues.includes('__manage__')
      const selectedTypes = nextValues.filter((type) => !['__create_new__', '__manage__'].includes(type))

      this.entry.projectTypes = selectedTypes
      this.entry.projectType = selectedTypes[0] || null

      if (shouldCreate) {
        this.showCreateProjectTypeDialog = true
      }

      if (shouldManage) {
        this.$router.push('/admin/projects')
      }
    },

    closeCreateProjectTypeDialog() {
      this.showCreateProjectTypeDialog = false
      this.resetProjectTypeForm()
    },

    resetProjectTypeForm() {
      this.newProjectType = {
        title: '',
        value: '',
        description: '',
      }
      if (this.$refs.projectTypeForm) {
        this.$refs.projectTypeForm.reset()
        this.$refs.projectTypeForm.resetValidation()
      }
    },

    async createProjectType() {
      if (!this.$refs.projectTypeForm.validate()) {
        return
      }

      this.creatingProjectType = true

      try {
        // Create the project type using the admin store
        const projectType = await this.$store.dispatch('admin/createProjectType', {
          title: this.newProjectType.title.trim(),
          value: this.newProjectType.value.trim(),
          description: this.newProjectType.description.trim(),
        })

        // Set the newly created project type as selected
        const currentTypes = Array.isArray(this.entry.projectTypes) ? this.entry.projectTypes.filter(Boolean) : []
        this.entry.projectTypes = [...new Set([...currentTypes, projectType.value])]
        this.entry.projectType = this.entry.projectTypes[0] || projectType.value

        // Show success message
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: `Project type "${projectType.title}" created successfully`,
          status: 'success',
        }, { root: true })

        // Close the dialog
        this.closeCreateProjectTypeDialog()
      } catch (error) {
        // Show error message
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: 'Failed to create project type. Please try again.',
          status: 'error',
        }, { root: true })
      } finally {
        this.creatingProjectType = false
      }
    },
    validateForm() {
      if (this.$refs.projectForm) {
        return this.$refs.projectForm.validate()
      }
      return true
    },

    reset() {
      if (this.$refs.projectForm) {
        return this.$refs.projectForm.reset()
      }
      return undefined
    },

    resetValidation() {
      if (this.$refs.projectForm) {
        return this.$refs.projectForm.resetValidation()
      }
      return undefined
    },

    closeForm() {
      try {
        this.resetValidation()
      } catch (e) {
        // Form may already be unmounted due to reactivity
        this.$log.warn('Could not reset form validation:', e.message)
      }
      // Always close the modal
      this.visible = false
      // Reset action state to ensure clean state for next open
      this.$store.commit('internal/action', 'read')
    },

    hideThresholdForm() {
      this.thresholdForm = null
    },
    showThresholdForm(action, entryThreshold = {}) {
      const testing = true

      if (testing) {
        return testing
      }

      if (action === 'update') {
        this.thresholdForm = entryThreshold
      } else {
        this.thresholdForm = {
          // Identifiers
          name: String,
          code: String, // User input of an alphanumeric value matching the TEST1234 pattern. Unique.

          // Change Logs
          updated: [{ date: Date, by: String }], // by === currentUser.email
          created: [{ date: Date, by: String }], // by === personUser.email

          // Dates
          startDate: Date,
          endDate: Date,

          watch: {
            collectionName: 'project', // Collection Name: String: [organization, person, project, service, team, time]
            property: 'projectCode', // Collection Code: String: [billingCode, personId, projectCode, serviceCode, teamCode]
            value: 'TEST1234', // A string to be used as a regex test on the given property, e.g. projectCode.
            operator: 'eq',
          },
          cause: [
            {
              dateRange: { startDate: Date, endDate: Date },
              // Update form input types and options based on context like page and user selections in the form.
              // Option Groups: [Boolean, Numerical, Textual, Strict (N/EQ), Custom]
              if: 'add', // Event: ['add', 'inactive', 'remove']
              is: true, // Value: Any: [user input, presets based on operator]
              for: 'yield partner', // ['demand source', 'network', 'yield partner']
              operator: 'and', // ['and', 'or']
            },
            {
              // @TODO Confirm operator at dateRange during user sessions.
              dateRange: { startDate: Date, endDate: Date }, // >= 14 days
              if: 'total',
              is: 0,
              for: 'delivery',
              operator: 'and', // ['and', 'or']
            },
          ],
          effect: [
            {
              then: 'email', // ['alert in-app', 'email', 'slack']
              endpoints: ['accounts@adops.com', 'bts@adops.com'], // [personUser.email]
              frequency: 'daily', // Frequency Cap: [once, daily, weekly, monthly]
              schedule: 'immediately', // [immediately, daily, weekly, monthly]
            },
            {
              then: 'email', // ['alert in-app', 'email', 'slack']
              endpoints: ['example.internal@adops.com'], // [personUser.email]
              frequency: 'once', // Frequency Cap: [daily, once]
              schedule: 'weekly', // [immediately, daily, weekly, monthly]
            },
          ],

          filter: [
            { operator: 'neq', property: 'name', value: 'Yield Partner A' },
            { operator: 'neq', property: 'type', value: 'Sponsorship' },
          ],
        }
      }

      return this.thresholdForm
    },
    hideProjectForm() {
      this.projectForm = null
    },
    showProjectForm(action, entryProject = {}) {
      if (action === 'update') {
        this.projectForm = entryProject
      } else {
        this.projectForm = {
          // Identifiers
          name: String,
          code: String,

          // Change Logs
          // Update change log separately to avoid performance issues.
          updated: [{
            // endpointName: 'project',
            // endpointCode: 'TEST2021',
            by: String, // by === currentUser.email
            date: Date,
            snapshot: String, // JSON.stringify(entry)
          }],

          // Dates
          startDate: Date,
          endDate: Date,

          contract: [
            {
              type: String, // []
              hours: Number,
            },
          ],
        }
      }
    },

    /**
     * @function submitProjectAction
     * @description Submits the project action (create, update, delete) to both Firebase and the API.
     * Validates delete confirmation for delete actions.
     * Maps action types to API expected values.
     * Prepares the project entry for Firebase by excluding unnecessary properties.
     * Dispatches the upsert action to Firebase and handles errors.
     * Dispatches the project action to the API and handles errors.
     * Provides user feedback via snackbars for success and error states.
     * @returns {Promise<Object>} The API response object.
     * @throws {Error} Throws an error if Firebase or API calls fail.
     */
    async submitProjectAction() {
      const { action, entry } = this
      const { commit, dispatch } = this.$store

      if (action !== 'delete') {
        const previousStatus = action === 'update' ? this.selected?.projectStatus : null
        const validation = validateEngagementLifecycleTransition({
          previousStatus,
          nextStatus: entry?.projectStatus,
          billingCode: entry?.billingCode,
          projectCode: entry?.projectCode,
          estimatedProjectValue: entry?.estimatedProjectValue,
          docs: entry?.docs,
          contractType: entry?.contractType,
          contractBillingType: entry?.contractBillingType,
        })

        if (!validation.isValid) {
          const message = validation.errors.join(' ')
          commit('tools/snackbar', true)
          commit('tools/snack', { message, status: 'error' })
          throw new Error(message)
        }
      }

      // Validate delete confirmation
      if (action === 'delete' && !this.isProjectDeleted) {
        // Open the confirmation dialog.
        this.deleteConfirmationError = true

        // Exit early with confirmation request.
        const error = false
        const content = []
        const message = 'Confirm Deletion'

        return { error, content, message }
      }

      this.deleteConfirmationError = false

      // Map action to the action_type expected by the API.
      const map = {
        create: 'ADD',
        update: 'UPDATE',
        delete: 'DELETE',
      }

      entry.action_type = map[action]

      /**
       * Send to Firebase.
       */

      const firestoreKeyMap = {
        projectCode: 'project_code',
        projectId: 'project_id',
        projectName: 'project_name',
      }
      const apiKeys = ['project_code', 'project_id', 'project_name']
      const firestoreKeys = Object.keys(firestoreKeyMap)
      const excludedProperties = [
        ...apiKeys,
        ...firestoreKeys,

        // Identifiers
        'docId',

        'created_dt',
        'updated_dt',

        // Data and Its Manipulation
        'action_type', // Used in API calls.
        'timeEntries', // Populated from Time API.
        'normalized', // Filter and sort helper.
        'notes', // Populated from Notes Collection in Firestore.

        // View State
        'isLoadingTimeEntries',

        // Calculated Fields
        'isActiveColor',
        'isActiveLabel',
        'projectDaysRemaining',
        'projectHref',
        'projectLength',

        'projectStatusColorBackground',
        'projectStatusColorText',

        // Formatted Dates
        'createdDateFormatted',
        'startDateFormatted',
        'endDateFormatted',
        'updatedDateFormatted',
      ]
      const firebaseDoc = Object.keys(entry).reduce((obj, key) => {
        const isFirestoreKey = firestoreKeyMap[key] !== undefined
        const isExcludedKey = excludedProperties.includes(key)

        if (isFirestoreKey) {
          const apiKey = `${firestoreKeyMap[key]}`

          obj[key] = entry[apiKey]
        }

        if (!isExcludedKey && entry[key] !== undefined && entry[key] !== null) {
          obj[key] = entry[key]
        }

        return obj
      }, {})
      const booleanFields = ['isActive', 'isArchived', 'isDeleted', 'isTest', 'isLinkedToSheet', 'isPublished']
      const getProjectTypeValue = () => (entry.projectType ? [entry.projectType] : [])
      const projectTypes = Array.isArray(entry.projectTypes) && entry.projectTypes.length > 0
        ? entry.projectTypes.filter(Boolean)
        : getProjectTypeValue()

      const docs = this.normalizeProjectDocs(entry.docs)

      firebaseDoc.projectTypes = projectTypes
      firebaseDoc.projectType = projectTypes[0] || null
      firebaseDoc.docs = docs

      entry.projectTypes = projectTypes
      entry.projectType = projectTypes[0] || null
      entry.docs = docs

      booleanFields.forEach((key) => {
        if (firebaseDoc[key] !== undefined) {
          firebaseDoc[key] = firebaseDoc[key] === true || firebaseDoc[key] === 'true'
        }
      })

      // Upsert the document to Firestore.
      const fireDoc = await this.$store.dispatch('internal/upsertProjectToFirestore', firebaseDoc)

      if (!fireDoc) {
        const message = 'No response from Firebase'

        commit('tools/snackbar', true)
        commit('tools/snack', { message, status: 'error' })

        throw new Error(message)
      }

      if (fireDoc.error) {
        // Inform the user of the error.
        const message = fireDoc.error.message || 'Unknown Firebase Error'

        commit('tools/snackbar', true)
        commit('tools/snack', { message, status: 'error' })

        throw new Error(message)
      }

      /**
       * Inform the user of the Firebase update.
       */

      commit('tools/snackbar', true)
      commit('tools/snack', {
        message: 'Applying ...',
        status: 'info',
      })

      /**
       * Send to API.
       */

      const apiResponse = await dispatch('internal/submitProjectAction', entry)
      const { content: apiContent = [], error: apiError } = apiResponse || {}

      if (apiError || apiContent?.code || !apiResponse) {
        // Inform the user of the error.
        const message = apiError?.message || apiContent?.message || apiContent?.code || 'Unknown API Error'

        commit('tools/snackbar', true)
        commit('tools/snack', { message, status: 'error' })

        // Exit.
        throw new Error(message)
      }

      // Set status message based on API response.
      const [entryId] = apiContent
      const apiStatus = entryId ? 'success' : 'error'
      const message = entryId ? this.successMessage : this.errorMessage

      if (apiStatus === 'success') {
        // Invalidate projects cache to ensure fresh data on next fetch
        api.invalidateCache('/projects')

        // Signal that data changed so the list refreshes on modal close
        commit('internal/projectModified', true, { root: true })

        // Close the form.
        this.closeForm()
      }

      // Inform user of the API response.
      commit('tools/snackbar', true)

      // For successful create and update events, provide the user with a quick link to view the project.
      if (apiStatus === 'success') {
        // Define a longer timeout to allow for navigation.
        const timeout = 10 * 1000 // 10 seconds

        // Show snackbar with action to view the project.
        commit('tools/snack', {
          message,
          status: apiStatus,
          action: {
            type: 'internal/goToProject',
            name: 'View Project',
            data: entry,
            timeout,
          },
        })
      } else {
        // Show error snackbar without action.
        commit('tools/snack', { message, status: apiStatus })
      }

      return apiResponse
    },

    /**
     * Event handler wrapper for submitProjectAction.
     * Catches errors to prevent unhandled Promise rejections from async
     * event handlers, which bypass Vue's errorHandler.
     */
    async handleSubmitProject() {
      // Clear previous form error
      this.showFormError = false

      // Validate form before submitting (for create/update actions)
      if (['create', 'update'].includes(this.action)) {
        const isValid = this.validateForm()
        if (!isValid) {
          this.showFormError = true
          return
        }
      }

      try {
        await this.submitProjectAction()
      } catch (error) {
        // Snackbar already shown in submitProjectAction
        // Log error for debugging; Sentry capture happens via snackbar context
        this.$log.error(error.message)
      }
    },

    /**
     * Submit the assign-client compact form as an update action.
     */
    async submitAssignClient() {
      if (!this.entry?.billingCode) return

      // Close the dialog before switching action to prevent the full form from flashing
      this.visible = false

      // Switch to update action for the submit flow
      this.$store.commit('internal/action', 'update')

      try {
        await this.submitProjectAction()
      } catch (error) {
        this.$log.error(error.message)
      }
    },

    cancelAction() {
      const { selected } = this

      this.$store.commit('internal/action', 'read')
      this.$store.commit('internal/entry', Object.create(selected))

      this.closeForm()
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~src/styles/variables";

::v-deep.v-card {
  border-radius: 6px;
  border-width: 0;
}

::v-deep.v-card__title {
  color: $color-monochrome-darkestblue;
  font-size: 18px;
  font-weight: bold;
  letter-spacing: 0.51px;
  line-height: 22px;
  justify-content: space-between;
}

::v-deep.v-card__text {
  padding: 0;
}

.v-card + .v-card {
  margin-top: 1ch;
}
</style>
